/**
 * PNG → labelmap parser for the multi-region dither pipeline.
 *
 * The dither-core's labelMap convention is a single-byte label index in
 * the red channel of an ImageData (green / blue / alpha ignored). Label
 * 0 is reserved for unlabelled / default-white pixels - any pixel whose
 * label has no matching entry in `regions[]` stays default-white.
 *
 * Artists author labelmaps in their existing image editor (Krita, PS,
 * Procreate, etc.) as a flat-coloured PNG sized to match the source.
 * `parseLabelmapImage` walks the PNG once, assigns labels 1..N to each
 * unique opaque RGB in scan order, and emits both the indexed
 * ImageData and the palette so the demo UI can show region rows.
 *
 * Anti-aliased input is **not** rejected here - a tiny AA fringe can
 * still pass the 64-colour cap. The format spec
 * (`docs/LABELMAP_FORMAT.md`) tells artists to use nearest-neighbour
 * resampling and indexed-colour PNG.
 */

import type { RegionConfig, RegionEntry, StatePatch } from '../state';
import { createResetSegmentationPatch } from '../state';
import { deltaE, srgbToLab } from './color-lab';
import { preprocessLabelmap } from './labelmap-preprocess';
import { generateUuid } from './uuid';

const MAX_REGION_COLOURS = 64;
const ALPHA_OPAQUE_THRESHOLD = 128;

/**
 * Carry-over statistics reported by `buildLabelmapPatch` when
 * `previousEntries` is supplied. Consumed by Item 15 (status warnings)
 * to surface the "Carried over N, dropped M, added K" status line.
 *
 * The stats are NOT stored in DemoState - they are ephemeral per-upload
 * data. Item 15 reads them from the returned LabelmapPatch before calling
 * `store.patch` with the StatePatch portion.
 *
 * `splits` counts old UUIDs that were within LAB delta-E threshold of
 * more than one new entry. When the greedy match picks a pair, any other
 * new entry that was also within threshold of the same old UUID is counted
 * as a split candidate. Reported via `status.labelmap.splits`.
 */
export type CarryOverStats = {
  carriedOver: number;
  dropped: number;
  added: number;
  /** Number of old UUIDs that matched multiple new entries below the LAB threshold (split candidates). */
  splits: number;
};

/**
 * Stats from an auto-preprocess pass (plan A7). Present when the raw
 * image had > 64 unique colours and was automatically reduced before
 * parsing. Null when no preprocessing was performed.
 */
export type PreprocessStats = {
  /** Unique coloured-pixel count in the raw input (excluding label-0 pool). */
  raw: number;
  /** Cluster count after k-means + optional post-merge. */
  resulting: number;
  /** True when `resulting == 64` (cap hit). */
  capHit: boolean;
};

/**
 * Return type of `buildLabelmapPatch`. Separates the pure StatePatch
 * (safe to pass directly to `store.patch`) from `carryOverStats`
 * (ephemeral; not stored in DemoState). Callers destructure to ensure
 * `carryOverStats` is never accidentally written into DemoState.
 *
 * `preprocessStats` is non-null when auto-preprocessing was performed
 * (raw > 64 unique colours). Used by the status renderer to show the
 * "Reduced from N to K" message alongside the carry-over stats.
 */
export type LabelmapPatchResult = {
  patch: StatePatch;
  carryOverStats: CarryOverStats;
  preprocessStats: PreprocessStats | null;
};

export type LabelmapErrorKey =
  | 'error.labelmap.dimensionMismatch'
  | 'error.labelmap.tooManyColours'
  | 'error.labelmap.invalidImage'
  | 'error.labelmap.empty';

export interface PaletteEntry {
  label: number;
  rgb: [number, number, number];
  pixelCount: number;
}

export interface ParsedLabelmap {
  labelMap: ImageData;
  palette: PaletteEntry[];
}

function rgbKey(r: number, g: number, b: number): number {
  return (r << 16) | (g << 8) | b;
}

/**
 * Convert a flat-coloured PNG (already decoded to ImageData) into the
 * red-channel-indexed ImageData the dither pipeline expects, plus the
 * palette of real region colours.
 *
 * Rules:
 *   - Fully transparent pixels (alpha < 128) → label 0 (unlabelled).
 *   - Opaque RGB(0, 0, 0) → label 0 (unlabelled).
 *   - Every other unique opaque RGB → labels 1, 2, … in scan order.
 *
 * Throws RangeError when the input contains more than 64 real region
 * colours. This is a colour-count guard, not anti-alias detection.
 */
export function parseLabelmapImage(src: ImageData): ParsedLabelmap {
  const w = src.width;
  const h = src.height;
  const total = w * h;
  const out = new ImageData(w, h);
  const palette: PaletteEntry[] = [];
  const keyToLabel = new Map<number, number>();

  for (let i = 0; i < total; i++) {
    const off = i * 4;
    const r = src.data[off] ?? 0;
    const g = src.data[off + 1] ?? 0;
    const b = src.data[off + 2] ?? 0;
    const a = src.data[off + 3] ?? 0;

    let label = 0;
    const isUnlabelled = a < ALPHA_OPAQUE_THRESHOLD || (r === 0 && g === 0 && b === 0);
    if (!isUnlabelled) {
      const key = rgbKey(r, g, b);
      const existing = keyToLabel.get(key);
      if (existing !== undefined) {
        label = existing;
        const entry = palette[existing - 1];
        if (entry) entry.pixelCount += 1;
      } else {
        if (palette.length >= MAX_REGION_COLOURS) {
          throw new RangeError(
            `labelmap has more than ${MAX_REGION_COLOURS} unique region colours`,
          );
        }
        label = palette.length + 1;
        keyToLabel.set(key, label);
        palette.push({ label, rgb: [r, g, b], pixelCount: 1 });
      }
    }

    out.data[off] = label;
    out.data[off + 1] = 0;
    out.data[off + 2] = 0;
    out.data[off + 3] = 255;
  }

  return { labelMap: out, palette };
}

/**
 * Derive a compact label-plane Uint8Array from a parsed labelmap ImageData.
 * The parsed labelmap stores label indices in the R channel (one label per
 * pixel). The output is one byte per pixel: regionLabelPlane[i] = label index.
 */
function deriveLabelPlane(labelMapData: ImageData): Uint8Array {
  const total = labelMapData.width * labelMapData.height;
  const plane = new Uint8Array(total);
  for (let i = 0; i < total; i++) {
    plane[i] = labelMapData.data[i * 4]!;
  }
  return plane;
}

/**
 * Build a lookup key from an RGB triple for use in exact-match carry-over.
 * Identical to the `rgbKey` function used during parsing but scoped here
 * for clarity.
 */
function entryRgbKey(rgb: [number, number, number]): number {
  return (rgb[0] << 16) | (rgb[1] << 8) | rgb[2];
}

// LAB delta-E threshold for the "thresholded nearest match" carry-over step
// (plan A3 step 2). Default 5.0 - colours within 5 delta-E are perceptually
// close enough to treat as the same region after a minor colour shift.
const LAB_CARRY_THRESHOLD = 5.0;

/**
 * Count the number of unique opaque non-black colours in an ImageData
 * (one pass, same rules as parseLabelmapImage). Used to decide whether
 * auto-preprocessing is needed before feeding the image to the parser.
 *
 * Returns the count of distinct [R, G, B] triples that would become
 * region colours (i.e. excluding transparent and pure-black pixels).
 */
function countUniqueRegionColours(src: ImageData): number {
  const total = src.width * src.height;
  const seen = new Set<number>();
  for (let i = 0; i < total; i++) {
    const off = i * 4;
    const r = src.data[off] ?? 0;
    const g = src.data[off + 1] ?? 0;
    const b = src.data[off + 2] ?? 0;
    const a = src.data[off + 3] ?? 0;
    if (a < ALPHA_OPAQUE_THRESHOLD) continue;
    if (r === 0 && g === 0 && b === 0) continue;
    seen.add(rgbKey(r, g, b));
  }
  return seen.size;
}

/**
 * Compose a state patch from a freshly decoded labelmap PNG and the
 * current source. The caller passes the StatePatch portion to
 * `store.patch` and may inspect `carryOverStats` and `preprocessStats`
 * for status display.
 *
 * The error branches deliberately leave `labelMap` and `regions`
 * untouched: a bad drop should not silently wipe the user's existing
 * multi-region setup. The exception is "empty palette" (transparent or
 * fully-black input), which sets `labelMap: null` + `regions: []` to
 * avoid pushing the renderer into the multi-region path with zero
 * entries.
 *
 * In addition to the pre-existing `labelMap` and `regions` fields, this
 * function now populates the v0.2.4 segmentation fields (`regionEntries`,
 * `regionLabelPlane`, `activeRegionUuids`). The old `regions[]` field and
 * the new `regionEntries[]` field are populated in parallel; Item 3 will
 * swap the bridge so `regions[]` is derived from `regionEntries[]`.
 *
 * **Part A - Auto-preprocess hook (plan A7):**
 * When `autoPreprocess` is true (default) and the raw image has more than
 * 64 unique region colours, `preprocessLabelmap` is automatically run with
 * the "Balanced" preset (`maxK: 32, deltaE: 4`) before parsing. The result
 * is reported in `preprocessStats`.
 *
 * **Part B - LAB-threshold carry-over (plan A3 step 2):**
 * After exact-RGB matching, remaining unmatched new entries are compared
 * against remaining unmatched old entries by CIE76 delta-E in LAB space.
 * Pairs within `LAB_CARRY_THRESHOLD` (5.0) are greedily matched (closest
 * first). Each old UUID is consumed at most once. Split candidates (old
 * UUIDs that were within threshold for multiple new entries) are counted
 * in `carryOverStats.splits`.
 *
 * When `previousEntries` is provided, exact-RGB matching carries over
 * UUID / userName / override / stackId from the old entries (plan A3,
 * "External labelmap re-upload" steps 1, 3, 4, 5).
 *
 * When `previousActiveUuids` is provided (Item 18), the returned
 * `activeRegionUuids` preserves the intersection of those UUIDs with the
 * surviving region entries. This ensures multi-select is preserved across
 * re-uploads where the selected regions still exist. Falls back to the
 * first entry if the intersection is empty.
 *
 * @param autoPreprocess  When true (default), auto-reduce images with > 64
 *   unique colours using the "Balanced" preset before parsing. Set false
 *   in tests to bypass preprocessing and test the pure carry-over logic.
 */
export function buildLabelmapPatch(
  image: ImageData,
  source: ImageData | null,
  previousEntries?: readonly RegionEntry[],
  formatDefaultName?: (label: number) => string,
  previousActiveUuids?: readonly string[],
  autoPreprocess = true,
): LabelmapPatchResult {
  // Sentinel stats returned on every error path so the type is satisfied.
  const zeroStats: CarryOverStats = { carriedOver: 0, dropped: 0, added: 0, splits: 0 };

  if (!source) {
    return {
      patch: { labelmapError: 'error.labelmap.dimensionMismatch' },
      carryOverStats: zeroStats,
      preprocessStats: null,
    };
  }
  if (image.width !== source.width || image.height !== source.height) {
    return {
      patch: { labelmapError: 'error.labelmap.dimensionMismatch' },
      carryOverStats: zeroStats,
      preprocessStats: null,
    };
  }

  // ── Part A: Auto-preprocess hook (plan A7) ──────────────────────────────
  //
  // Count unique region colours in one pass. If > 64 and autoPreprocess is
  // enabled, run preprocessLabelmap with the "Balanced" preset (A7.1) before
  // feeding the image to parseLabelmapImage. This avoids the RangeError that
  // parseLabelmapImage would throw for > 64-colour input.
  let preprocessStats: PreprocessStats | null = null;
  let imageToparse: ImageData = image;

  if (autoPreprocess) {
    const rawUniqueColours = countUniqueRegionColours(image);
    if (rawUniqueColours > MAX_REGION_COLOURS) {
      // "Balanced" preset: maxK=32, deltaE=4 (plan A7.1).
      const result = preprocessLabelmap(image, { maxK: 32, deltaE: 4 });
      imageToparse = result.output;
      preprocessStats = {
        raw: rawUniqueColours,
        resulting: result.resultingClusters,
        capHit: result.resultingClusters >= MAX_REGION_COLOURS,
      };
    }
  }

  let parsed: ParsedLabelmap;
  try {
    parsed = parseLabelmapImage(imageToparse);
  } catch (err) {
    if (err instanceof RangeError) {
      return {
        patch: { labelmapError: 'error.labelmap.tooManyColours' },
        carryOverStats: zeroStats,
        preprocessStats,
      };
    }
    return {
      patch: { labelmapError: 'error.labelmap.invalidImage' },
      carryOverStats: zeroStats,
      preprocessStats,
    };
  }
  if (parsed.palette.length === 0) {
    // Empty palette: fully reset all segmentation state via the canonical
    // factory so stale stacks, undo history, and refloodMode are cleared.
    // Only labelmapError differs from the factory default (it carries the
    // 'empty' marker rather than null so the UI can show the right message).
    return {
      patch: {
        ...createResetSegmentationPatch(),
        labelmapError: 'error.labelmap.empty' as const,
      },
      carryOverStats: zeroStats,
      preprocessStats,
    };
  }
  const regions: RegionConfig[] = parsed.palette.map((entry) => ({
    label: entry.label,
    rgb: entry.rgb,
    override: null,
  }));

  // ── Exact-RGB carry-over (plan A3 "External labelmap re-upload", step 1) ──
  //
  // Build a lookup map from the previous entries by packed RGB key.
  // Each old entry can be consumed at most once (step 3).
  const prevByRgb = new Map<number, RegionEntry>();
  if (previousEntries && previousEntries.length > 0) {
    for (const prev of previousEntries) {
      const key = entryRgbKey(prev.rgb);
      // First occurrence wins; duplicates (shouldn't exist in practice) are ignored.
      if (!prevByRgb.has(key)) {
        prevByRgb.set(key, prev);
      }
    }
  }

  let carriedOver = 0;
  let added = 0;

  // Build v0.2.4 segmentation fields in parallel with the old regions[].
  // First pass: exact-RGB matches and collect unmatched new palette entries.
  const unmatchedNewEntries: Array<{ index: number; paletteEntry: PaletteEntry }> = [];
  const regionEntries: RegionEntry[] = parsed.palette.map((entry, index) => {
    const key = entryRgbKey(entry.rgb);
    const match = prevByRgb.get(key);
    if (match !== undefined) {
      // Exact-RGB match: reuse UUID, userName, override, stackId from the
      // old entry. The label index may have shifted (scan-order may differ
      // across re-uploads), so we update label to the new parse result.
      prevByRgb.delete(key); // consume the old entry (each UUID at most once)
      carriedOver += 1;
      return {
        uuid: match.uuid,
        label: entry.label,
        rgb: entry.rgb,
        userName: match.userName,
        override: match.override,
        stackId: match.stackId,
      };
    }
    // No exact match: mark for LAB carry-over pass, placeholder for now.
    unmatchedNewEntries.push({ index, paletteEntry: entry });
    return {
      uuid: '', // filled in the carry-over pass or below
      label: entry.label,
      rgb: entry.rgb,
      userName: formatDefaultName ? formatDefaultName(entry.label) : `Area ${entry.label}`,
      override: null,
      stackId: null,
    };
  });

  // ── Part B: LAB-threshold carry-over (plan A3 step 2) ──────────────────
  //
  // Remaining unmatched old entries (still in prevByRgb) versus unmatched
  // new entries. Build all pairwise delta-E distances and greedily match
  // the closest pair below LAB_CARRY_THRESHOLD. Each old UUID consumed once.
  let splits = 0;

  if (unmatchedNewEntries.length > 0 && prevByRgb.size > 0) {
    // Convert remaining old entries to an array for distance computation.
    const unmatchedOld = Array.from(prevByRgb.values());

    // Pre-compute LAB for unmatched new palette entries.
    const newLab = unmatchedNewEntries.map((n) => srgbToLab(n.paletteEntry.rgb));

    // Pre-compute LAB for remaining old entries.
    const oldLab = unmatchedOld.map((o) => srgbToLab(o.rgb));

    // Track which indices are still available.
    const availableNew = new Set(unmatchedNewEntries.map((_, i) => i));
    const availableOld = new Set(unmatchedOld.map((_, i) => i));

    // Greedily match closest pair below threshold until none remain.
    let progress = true;
    while (progress && availableNew.size > 0 && availableOld.size > 0) {
      progress = false;
      let bestDist = LAB_CARRY_THRESHOLD;
      let bestNewIdx = -1;
      let bestOldIdx = -1;

      for (const ni of availableNew) {
        for (const oi of availableOld) {
          const d = deltaE(newLab[ni]!, oldLab[oi]!);
          if (d < bestDist) {
            bestDist = d;
            bestNewIdx = ni;
            bestOldIdx = oi;
          }
        }
      }

      if (bestNewIdx !== -1 && bestOldIdx !== -1) {
        // Count splits: how many other available new entries were also
        // within threshold of this old entry?
        let splitCandidates = 0;
        for (const ni of availableNew) {
          if (ni === bestNewIdx) continue;
          if (deltaE(newLab[ni]!, oldLab[bestOldIdx]!) < LAB_CARRY_THRESHOLD) {
            splitCandidates += 1;
          }
        }
        if (splitCandidates > 0) splits += 1;

        // Apply the carry-over: patch the placeholder entry.
        const matched = unmatchedOld[bestOldIdx]!;
        const newEntry = unmatchedNewEntries[bestNewIdx]!;
        const existingEntry = regionEntries[newEntry.index]!;
        regionEntries[newEntry.index] = {
          uuid: matched.uuid,
          label: existingEntry.label,
          rgb: existingEntry.rgb,
          userName: matched.userName,
          override: matched.override,
          stackId: matched.stackId,
        };
        carriedOver += 1;

        // Remove the matched pair from the available sets.
        availableNew.delete(bestNewIdx);
        availableOld.delete(bestOldIdx);
        // Remove from prevByRgb to mark as consumed.
        prevByRgb.delete(entryRgbKey(matched.rgb));

        progress = true;
      }
    }
  }

  // Remaining unmatched new entries get fresh UUIDs (step 4 - leftover new → fresh).
  for (const { index } of unmatchedNewEntries) {
    const entry = regionEntries[index]!;
    if (entry.uuid === '') {
      regionEntries[index] = { ...entry, uuid: generateUuid() };
      added += 1;
    }
  }

  // Remaining entries in prevByRgb were not matched → dropped (step 5).
  const dropped = prevByRgb.size;

  // Compute activeRegionUuids: preserve intersection with surviving UUIDs when
  // previousActiveUuids is provided (Item 18 - multi-select carry-over). Fall
  // back to the first entry if the intersection is empty.
  let activeRegionUuids: string[];
  if (previousActiveUuids && previousActiveUuids.length > 0) {
    const survivingUuidSet = new Set(regionEntries.map((e) => e.uuid));
    const preserved = previousActiveUuids.filter((id) => survivingUuidSet.has(id));
    if (preserved.length > 0) {
      activeRegionUuids = preserved;
    } else {
      const firstUuid = regionEntries[0]?.uuid;
      activeRegionUuids = firstUuid !== undefined ? [firstUuid] : [];
    }
  } else {
    const firstUuid = regionEntries[0]?.uuid;
    activeRegionUuids = firstUuid !== undefined ? [firstUuid] : [];
  }

  return {
    patch: {
      labelMap: parsed.labelMap,
      regions,
      labelmapError: null,
      regionEntries,
      regionLabelPlane: deriveLabelPlane(parsed.labelMap),
      activeRegionUuids,
      sourceAdjustScope: 'region',
      // Store the raw original for Remap (plan A3 internal remap, Item 22).
      originalLabelmapImage: image,
    },
    carryOverStats: { carriedOver, dropped, added, splits },
    preprocessStats,
  };
}
